import { useCallback, useMemo, useState } from 'react';
import TopMenu from 'components/InventoryModifiers/TopMenu';
import List from 'components/common/ListWithMoreOptions';
import ModalFullScreen from 'components/common/ModalFullScreen';
import { useItemModifiers } from 'graphql/useItemExtras';
import { useItemModifierMutations } from 'graphql/useItemExtraMutations';
import { Spin } from 'antd';
import moment from "moment";
import BottomPageSelectorWithPagination, {
  usePagination,
} from 'components/common/BottomPageSelectorWithPagination';
import { usePaginatedData } from 'helpers/hooks';
import { useMessaging } from 'context/MessagingContext';
import { useTranslation } from 'react-i18next';
import s from './styles';
import AddModifier from './AddModifier';

function InventoryModifiers() {
  const { t } = useTranslation();
  const [addModifierModalOpen, setAddItemModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);

  const { deleteItemModifier } = useItemModifierMutations();
  const { setInfoMessage, setConfirmMessage, setErrorMessage } = useMessaging();

  const { sorting, setSorting, search, setSearch, pagination, setPagination } =
    usePagination('name');

  function changeSearch(v) {
    setSearch(v);
    setPagination({ ...pagination, page: 0 });
  }

  const { itemModifiers, loading } = useItemModifiers();

  const data = useMemo(() => Object.values(itemModifiers).filter((c) =>
    c.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
  ), [itemModifiers, search]);

  const dataToShow = usePaginatedData(data, pagination, sorting, (item) => [
    item.name,
    item.modifiers?.map((modifier) => modifier.name).join(', '),
    moment(item.updateDatetime).format("lll"),
  ]);

  const onUpdateSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t('containers.InventoryModifiers.modifierEdit'),
      info1: t('containers.InventoryModifiers.findModifier'),
    });
  }, []);
  const onAddSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t('containers.InventoryModifiers.newModifier'),
      info1: t('containers.InventoryModifiers.modifierDB'),
    });
  }, []);

  const onDeleteRequest = useCallback((modifier) => {
    setConfirmMessage({
      titleAlert: t('containers.InventoryModifiers.delete'),
      confirm1: `${t('containers.InventoryModifiers.deleteModifier')} ${
        modifier.name
      }? `,
      confirm2: t('containers.InventoryModifiers.actionCannotBeUndone'),
      onConfirm: () =>
        deleteItemModifier(modifier)
          .then(() => {})
          .catch((e) => setErrorMessage({ error: e })),
    });
  }, []);

  return (
    <s.PageContainer>
      <TopMenu
        changeSearchValue={(v) => changeSearch(v)}
        addModifiers={() => setAddItemModalOpen(true)}
        loading={loading}
      />
      {!loading && (
        <s.ListContainer>
          <List
            width="500px"
            handleEdit={(mofifiers) => {
              const copy = JSON.parse(JSON.stringify(mofifiers.element));
              function cleanCopy(copy) {
                delete copy.__typename;
                copy.modifiers?.forEach((modifier) => {
                  delete modifier.__typename;
                });
              }
              cleanCopy(copy);
              setItemToEdit(copy);
              setAddItemModalOpen(true);
            }}
            handleDeleteWorker={(category) => {
              onDeleteRequest(category.element);
            }}
            data={dataToShow}
            columns={[
              {
                title: t('containers.InventoryModifiers.modifier'),
                sortable: true,
                columnId: 'name',
              },
              {
                title: t('containers.InventoryModifiers.options'),
              },
              {
                title: t('containers.InventoryModifiers.updateDatetime'),
                sortable: true,
                columnId: 'updateDatetime',
              },
              {
                title: '',
              },
            ]}
            sorting={sorting}
            changeSorting={(columnId, order) => setSorting({ columnId, order })}
            tableStyle={{ gridTemplateColumns: '10fr 15fr 15fr 3fr' }}
          />
        </s.ListContainer>
      )}
      {loading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
          }}
        >
          <Spin
            style={{ color: '#000' }}
            size="large"
            tip={t('containers.InventoryModifiers.loading')}
          />
        </div>
      )}

      <BottomPageSelectorWithPagination
        pagination={pagination}
        setPagination={setPagination}
        length={itemModifiers.length}
      />

      {addModifierModalOpen && (
        <ModalFullScreen>
          <AddModifier
            closeModal={() => {
              setAddItemModalOpen(false);
              setItemToEdit(null);
            }}
            editItem={itemToEdit}
            onUpdateSuccess={onUpdateSuccess}
            onAddSuccess={onAddSuccess}
            setErrorMessage={setErrorMessage}
          />
        </ModalFullScreen>
      )}
    </s.PageContainer>
  );
}

export default InventoryModifiers;
